import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { findSuggestionMatch as defaultFindSuggestionMatch } from './findSuggestionMatch';
export const SuggestionPluginKey = new PluginKey('suggestion');
/**
 * This utility allows you to create suggestions.
 * @see https://tiptap.dev/api/utilities/suggestion
 */ export function Suggestion({ pluginKey = SuggestionPluginKey, editor, char = '@', allowSpaces = false, allowedPrefixes = [
    ' '
], startOfLine = false, decorationTag = 'span', decorationClass = 'suggestion', command = ()=>null, items = ()=>[], render = ()=>({}), allow = ()=>true, findSuggestionMatch = defaultFindSuggestionMatch }) {
    let props;
    const renderer = render === null || render === void 0 ? void 0 : render();
    const plugin = new Plugin({
        key: pluginKey,
        view () {
            return {
                update: async (view, prevState)=>{
                    var _this_key, _this_key1;
                    const prev = (_this_key = this.key) === null || _this_key === void 0 ? void 0 : _this_key.getState(prevState);
                    const next = (_this_key1 = this.key) === null || _this_key1 === void 0 ? void 0 : _this_key1.getState(view.state);
                    // See how the state changed
                    const moved = prev.active && next.active && prev.range.from !== next.range.from;
                    const started = !prev.active && next.active;
                    const stopped = prev.active && !next.active;
                    const changed = !started && !stopped && prev.query !== next.query;
                    const handleStart = started || moved;
                    const handleChange = changed && !moved;
                    const handleExit = stopped || moved;
                    // Cancel when suggestion isn't active
                    if (!handleStart && !handleChange && !handleExit) {
                        return;
                    }
                    const state = handleExit && !handleStart ? prev : next;
                    const decorationNode = view.dom.querySelector(`[data-decoration-id="${state.decorationId}"]`);
                    props = {
                        editor,
                        range: state.range,
                        query: state.query,
                        text: state.text,
                        items: [],
                        command: (commandProps)=>{
                            return command({
                                editor,
                                range: state.range,
                                props: commandProps
                            });
                        },
                        decorationNode,
                        // virtual node for popper.js or tippy.js
                        // this can be used for building popups without a DOM node
                        clientRect: decorationNode ? ()=>{
                            var _this_key;
                            // because of `items` can be asynchrounous we’ll search for the current decoration node
                            const { decorationId } = (_this_key = this.key) === null || _this_key === void 0 ? void 0 : _this_key.getState(editor.state); // eslint-disable-line
                            const currentDecorationNode = view.dom.querySelector(`[data-decoration-id="${decorationId}"]`);
                            return (currentDecorationNode === null || currentDecorationNode === void 0 ? void 0 : currentDecorationNode.getBoundingClientRect()) || null;
                        } : null
                    };
                    if (handleStart) {
                        var _renderer_onBeforeStart;
                        renderer === null || renderer === void 0 ? void 0 : (_renderer_onBeforeStart = renderer.onBeforeStart) === null || _renderer_onBeforeStart === void 0 ? void 0 : _renderer_onBeforeStart.call(renderer, props);
                    }
                    if (handleChange) {
                        var _renderer_onBeforeUpdate;
                        renderer === null || renderer === void 0 ? void 0 : (_renderer_onBeforeUpdate = renderer.onBeforeUpdate) === null || _renderer_onBeforeUpdate === void 0 ? void 0 : _renderer_onBeforeUpdate.call(renderer, props);
                    }
                    if (handleChange || handleStart) {
                        props.items = await items({
                            editor,
                            query: state.query
                        });
                    }
                    if (handleExit) {
                        var _renderer_onExit;
                        renderer === null || renderer === void 0 ? void 0 : (_renderer_onExit = renderer.onExit) === null || _renderer_onExit === void 0 ? void 0 : _renderer_onExit.call(renderer, props);
                    }
                    if (handleChange) {
                        var _renderer_onUpdate;
                        renderer === null || renderer === void 0 ? void 0 : (_renderer_onUpdate = renderer.onUpdate) === null || _renderer_onUpdate === void 0 ? void 0 : _renderer_onUpdate.call(renderer, props);
                    }
                    if (handleStart) {
                        var _renderer_onStart;
                        renderer === null || renderer === void 0 ? void 0 : (_renderer_onStart = renderer.onStart) === null || _renderer_onStart === void 0 ? void 0 : _renderer_onStart.call(renderer, props);
                    }
                },
                destroy: ()=>{
                    var _renderer_onExit;
                    if (!props) {
                        return;
                    }
                    renderer === null || renderer === void 0 ? void 0 : (_renderer_onExit = renderer.onExit) === null || _renderer_onExit === void 0 ? void 0 : _renderer_onExit.call(renderer, props);
                }
            };
        },
        state: {
            // Initialize the plugin's internal state.
            init () {
                const state = {
                    active: false,
                    range: {
                        from: 0,
                        to: 0
                    },
                    query: null,
                    text: null,
                    composing: false
                };
                return state;
            },
            // Apply changes to the plugin state from a view transaction.
            apply (transaction, prev, oldState, state) {
                const { isEditable } = editor;
                const { composing } = editor.view;
                const { selection } = transaction;
                const { empty, from } = selection;
                const next = _object_spread({}, prev);
                next.composing = composing;
                // We can only be suggesting if the view is editable, and:
                //   * there is no selection, or
                //   * a composition is active (see: https://github.com/ueberdosis/tiptap/issues/1449)
                if (isEditable && (empty || editor.view.composing)) {
                    // Reset active state if we just left the previous suggestion range
                    if ((from < prev.range.from || from > prev.range.to) && !composing && !prev.composing) {
                        next.active = false;
                    }
                    // Try to match against where our cursor currently is
                    const match = findSuggestionMatch({
                        char,
                        allowSpaces,
                        allowedPrefixes,
                        startOfLine,
                        $position: selection.$from
                    });
                    const decorationId = `id_${Math.floor(Math.random() * 0xffffffff)}`;
                    // If we found a match, update the current state to show it
                    if (match && allow({
                        editor,
                        state,
                        range: match.range
                    })) {
                        next.active = true;
                        next.decorationId = prev.decorationId ? prev.decorationId : decorationId;
                        next.range = match.range;
                        next.query = match.query;
                        next.text = match.text;
                    } else {
                        next.active = false;
                    }
                } else {
                    next.active = false;
                }
                // Make sure to empty the range if suggestion is inactive
                if (!next.active) {
                    next.decorationId = null;
                    next.range = {
                        from: 0,
                        to: 0
                    };
                    next.query = null;
                    next.text = null;
                }
                return next;
            }
        },
        props: {
            // Call the keydown hook if suggestion is active.
            handleKeyDown (view, event) {
                var _renderer_onKeyDown;
                const { active, range, query } = plugin.getState(view.state);
                if (!active) {
                    return false;
                }
                return (renderer === null || renderer === void 0 ? void 0 : (_renderer_onKeyDown = renderer.onKeyDown) === null || _renderer_onKeyDown === void 0 ? void 0 : _renderer_onKeyDown.call(renderer, {
                    view: view,
                    event,
                    range,
                    editorCommands: editor.commands,
                    query
                })) || false;
            },
            // Setup decorator on the currently active suggestion.
            decorations (state) {
                const { active, range, decorationId } = plugin.getState(state);
                if (!active) {
                    return null;
                }
                return DecorationSet.create(state.doc, [
                    Decoration.inline(range.from, range.to, {
                        nodeName: decorationTag,
                        class: decorationClass,
                        'data-decoration-id': decorationId
                    })
                ]);
            }
        }
    });
    return plugin;
}

//# sourceMappingURL=suggestion.js.map